import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTenant } from './TenantContext';
import {
    loginWithPublicPassword,
    PublicAuthIntent,
    PublicAuthOtpRequestResponse,
    PublicAuthSession,
    PublicAuthVerifyPayload,
    requestPublicAuthCode,
    verifyPublicAuthCode,
} from '../services/publicAuthService';
import {
    clearSession,
    loadSession,
    resolveTenantKey,
    saveSession,
    StoredPublicAuthSession,
} from '../utils/authToken';

export interface PublicAuthUser {
    userId: number;
    email: string;
    fullName?: string | null;
    tenantId: number;
    hasPassword: boolean;
    mustChangePassword: boolean;
}

export interface PublicAuthModalState {
    open: boolean;
    intent: PublicAuthIntent;
    email?: string;
}

interface PublicAuthContextValue {
    user: PublicAuthUser | null;
    token: string | null;
    expiresAt: number | null;
    hasPassword: boolean;
    mustChangePassword: boolean;
    isAuthenticated: boolean;
    loadingSession: boolean;
    modal: PublicAuthModalState;
    openModal: (intent?: PublicAuthIntent, email?: string) => void;
    closeModal: () => void;
    requestCode: (intent: PublicAuthIntent, email: string) => Promise<PublicAuthOtpRequestResponse>;
    verifyCode: (payload: PublicAuthVerifyPayload) => Promise<PublicAuthSession>;
    loginWithPassword: (email: string, password: string) => Promise<PublicAuthSession>;
    logout: () => void;
    tenantKey: string | null;
}

const PublicAuthContext = createContext<PublicAuthContextValue | undefined>(undefined);

const defaultModal: PublicAuthModalState = {
    open: false,
    intent: 'LOGIN',
    email: undefined,
};

export const PublicAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const tenant = useTenant();
    const [session, setSession] = useState<StoredPublicAuthSession | null>(null);
    const [modal, setModal] = useState<PublicAuthModalState>(defaultModal);
    const [loadingSession, setLoadingSession] = useState(true);

    const tenantKey = useMemo(() => resolveTenantKey(tenant?.tenantSlug, tenant?.tenantId), [tenant?.tenantSlug, tenant?.tenantId]);

    const hydrateFromStorage = useCallback(() => {
        if (!tenantKey) {
            setSession(null);
            setLoadingSession(false);
            return;
        }
        const stored = loadSession(tenantKey);
        if (stored) {
            setSession(stored);
        } else {
            setSession(null);
        }
        setLoadingSession(false);
    }, [tenantKey]);

    useEffect(() => {
        setLoadingSession(true);
        hydrateFromStorage();
    }, [hydrateFromStorage]);

    useEffect(() => {
        if (!session?.expiresAt) return;
        const remaining = session.expiresAt - Date.now();
        if (remaining <= 0) {
            setSession(null);
            if (tenantKey) clearSession(tenantKey);
            return;
        }
        const timeout = window.setTimeout(() => {
            setSession(null);
            if (tenantKey) clearSession(tenantKey);
        }, remaining);
        return () => window.clearTimeout(timeout);
    }, [session?.expiresAt, tenantKey]);

    const persistSession = useCallback((data: PublicAuthSession) => {
        if (!tenantKey) return data;
        const stored: StoredPublicAuthSession = {
            token: data.token,
            expiresAt: data.expiresAt,
            user: {
                userId: data.userId,
                email: data.email,
                fullName: data.fullName,
                hasPassword: data.hasPassword,
                mustChangePassword: data.mustChangePassword,
                tenantId: data.tenantId,
            },
        };
        saveSession(tenantKey, stored);
        setSession(stored);
        return data;
    }, [tenantKey]);

    const openModal = useCallback((intent: PublicAuthIntent = 'LOGIN', email?: string) => {
        setModal({ open: true, intent, email });
    }, []);

    const closeModal = useCallback(() => {
        setModal(defaultModal);
    }, []);

    const ensureTenantIdentifier = useCallback((): string => {
        if (tenant?.tenantSlug && tenant.tenantSlug.trim()) return tenant.tenantSlug;
        if (tenant?.tenantId && tenant.tenantId.trim()) return tenant.tenantId;
        throw new Error('TENANT_IDENTIFIER_MISSING');
    }, [tenant?.tenantSlug, tenant?.tenantId]);

    const requestCode = useCallback(async (intent: PublicAuthIntent, email: string) => {
        const tenantIdentifier = ensureTenantIdentifier();
        return requestPublicAuthCode(tenantIdentifier, { intent, email }, tenant?.tenantId);
    }, [ensureTenantIdentifier, tenant?.tenantId]);

    const verifyCode = useCallback(async (payload: PublicAuthVerifyPayload) => {
        const tenantIdentifier = ensureTenantIdentifier();
        const result = await verifyPublicAuthCode(
            tenantIdentifier,
            payload,
            tenant?.tenantId
        );
        persistSession(result);
        closeModal();
        return result;
    }, [ensureTenantIdentifier, tenant?.tenantId, persistSession, closeModal]);

    const loginPassword = useCallback(async (email: string, password: string) => {
        const tenantIdentifier = ensureTenantIdentifier();
        const result = await loginWithPublicPassword(
            tenantIdentifier,
            { email, password },
            tenant?.tenantId
        );
        persistSession(result);
        closeModal();
        return result;
    }, [ensureTenantIdentifier, tenant?.tenantId, persistSession, closeModal]);

    const logout = useCallback(() => {
        setSession(null);
        if (tenantKey) clearSession(tenantKey);
    }, [tenantKey]);

    const contextValue = useMemo<PublicAuthContextValue>(() => {
        const user = session?.user ?? null;
        return {
            user,
            token: session?.token ?? null,
            expiresAt: session?.expiresAt ?? null,
            hasPassword: Boolean(user?.hasPassword),
            mustChangePassword: Boolean(user?.mustChangePassword),
            isAuthenticated: Boolean(session?.token),
            loadingSession,
            modal,
            openModal,
            closeModal,
            requestCode,
            verifyCode,
            loginWithPassword: loginPassword,
            logout,
            tenantKey,
        };
    }, [session, loadingSession, modal, openModal, closeModal, requestCode, verifyCode, loginPassword, logout, tenantKey]);

    return (
        <PublicAuthContext.Provider value={contextValue}>
            {children}
        </PublicAuthContext.Provider>
    );
};

export const usePublicAuth = () => {
    const ctx = useContext(PublicAuthContext);
    if (!ctx) {
        throw new Error('usePublicAuth must be used within PublicAuthProvider');
    }
    return ctx;
};
